import React, { useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';

// Success Page Component
const SuccessPage = ({ data }) => {
    // Determine data structure (handle both camelCase and PascalCase)
    const d = data.data || data.Data || data;
    const userId = d.userId || d.UserId || 'Unknown';
    const firstName = d.firstName || d.FirstName || '';
    const lastName = d.lastName || d.LastName || '';
    const email = d.email || d.Email || '';
    const phoneNumber = d.phoneNumber || d.PhoneNumber || '';
    const shopName = d.shopName || d.ShopName || '';
    const address = d.address || d.Address || '';
    const city = d.city || d.City || '';
    const state = d.state || d.State || '';
    const pincode = d.pincode || d.Pincode || '';

    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-pink-50 py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto">
                <div className="bg-white rounded-3xl shadow-2xl overflow-hidden transform transition-all duration-500 hover:scale-[1.01]">
                    {/* Master Success Header */}
                    <div className="bg-gradient-to-r from-emerald-500 to-teal-600 p-10 text-center text-white relative">
                        <div className="absolute top-0 left-0 w-full h-full opacity-10 pointer-events-none">
                            <i className="fas fa-check-circle text-[200px] -ml-20 -mt-10"></i>
                        </div>
                        <div className="mb-6 inline-flex items-center justify-center w-24 h-24 bg-white bg-opacity-20 rounded-full backdrop-blur-sm border-4 border-white border-opacity-30 shadow-xl">
                            <i className="fas fa-check text-5xl"></i>
                        </div>
                        <h2 className="text-4xl font-extrabold mb-3 tracking-tight">Account Created Successfully!</h2>
                        {/* <p className="text-xl opacity-90 font-medium">Welcome to the RetailSaathi B2B Family</p> */}
                    </div>

                    <div className="p-8 md:p-12">
                        {/* Status Message */}
                        <div className="text-center mb-10">
                            <p className="text-gray-600 text-lg">
                                Your retailer account has been set up. You can now explore thousands of products at wholesale prices.
                            </p>
                        </div>

                        {/* User ID Section - Premium Card */}
                        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-100 p-8 mb-12 rounded-2xl shadow-inner flex flex-col md:flex-row items-center justify-between">
                            <div className="flex items-center mb-4 md:mb-0">
                                <div className="p-4 bg-blue-600 text-white rounded-2xl shadow-lg mr-6">
                                    <i className="fas fa-id-card-alt text-3xl"></i>
                                </div>
                                <div>
                                    <p className="text-sm text-blue-600 font-bold uppercase tracking-widest mb-1">Your User ID</p>
                                    <p className="text-2xl font-black text-gray-900 break-all">
                                        {email}
                                    </p>
                                    <p className="text-xs text-gray-500 mt-1">Please use your email to login</p>
                                </div>
                            </div>
                            <div className="flex flex-col items-center justify-center border-t md:border-t-0 md:border-l border-blue-200 pt-4 md:pt-0 md:pl-8">
                                <div className="text-2xl font-bold text-gray-800 mb-1">Status</div>
                                <div className="flex items-center text-emerald-600 font-bold px-4 py-1 bg-emerald-100 rounded-full text-sm">
                                    <span className="w-2 h-2 bg-emerald-500 rounded-full mr-2 animate-pulse"></span>
                                    Active
                                </div>
                            </div>
                        </div>

                        {/* Account Details Grid */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 mb-12">
                            <div className="space-y-6">
                                <h3 className="text-xl font-bold text-gray-900 flex items-center mb-6">
                                    <span className="w-1.5 h-6 bg-primary-500 rounded-full mr-3"></span>
                                    Personal Information
                                </h3>
                                <div className="space-y-4">
                                    <DetailItem label="Full Name" value={`${firstName} ${lastName}`} icon="fa-user-circle" />
                                    <DetailItem label="Email Address" value={email} icon="fa-envelope-open" />
                                    <DetailItem label="Mobile Number" value={phoneNumber} icon="fa-phone-alt" />
                                </div>
                            </div>
                            <div className="space-y-6">
                                <h3 className="text-xl font-bold text-gray-900 flex items-center mb-6">
                                    <span className="w-1.5 h-6 bg-pink-500 rounded-full mr-3"></span>
                                    Business Information
                                </h3>
                                <div className="space-y-4">
                                    <DetailItem label="Shop Name" value={shopName} icon="fa-store-alt" />
                                    <DetailItem label="Address" value={address} icon="fa-map-marked-alt" />
                                    <DetailItem label="Location" value={`${city}, ${state} - ${pincode}`} icon="fa-city" />
                                </div>
                            </div>
                        </div>

                        {/* Important Note */}
                        <div className="bg-amber-50 rounded-xl p-5 mb-12 border border-amber-100 flex items-start">
                            <i className="fas fa-info-circle text-amber-500 text-xl mr-4 mt-0.5"></i>
                            <p className="text-sm text-amber-800">
                                <strong>Note:</strong> We have sent a confirmation email to {email}. Please check your inbox (and spam folder) for further instructions.
                            </p>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex flex-col sm:flex-row gap-6 justify-center">
                            <a
                                href="/"
                                className="group relative bg-white border-2 border-primary-500 text-primary-500 hover:bg-primary-50 px-10 py-4 rounded-2xl font-bold transition-all duration-300 text-center shadow-lg"
                            >
                                <i className="fas fa-home mr-2 group-hover:-translate-x-1 transition-transform"></i>
                                Back to Portal
                            </a>
                            <a
                                href="/Account/Login"
                                className="group relative bg-gradient-to-r from-primary-600 to-pink-600 hover:from-primary-700 hover:to-pink-700 text-white px-10 py-4 rounded-2xl font-bold transition-all duration-300 text-center shadow-2xl hover:scale-105 active:scale-95"
                            >
                                <i className="fas fa-rocket mr-2 group-hover:translate-x-1 transition-transform"></i>
                                Explore Dashboard
                            </a>
                        </div>
                    </div>

                    {/* Footer decoration */}
                    <div className="h-2 bg-gradient-to-r from-primary-500 via-pink-500 to-teal-500 w-full"></div>
                </div>

                {/* Help Text */}
                <div className="text-center mt-10">
                    <p className="text-gray-500 text-sm">
                        Need help? Contact our support at <span className="text-primary-500 font-semibold cursor-pointer">support@retailsaathi.com</span>
                    </p>
                </div>
            </div>
        </div>
    );
};

const DetailItem = ({ label, value, icon }) => (
    <div className="flex items-start group">
        <div className="w-10 h-10 flex items-center justify-center bg-gray-50 rounded-xl text-gray-400 group-hover:bg-primary-50 group-hover:text-primary-500 transition-colors mr-4 shadow-sm">
            <i className={`fas ${icon} text-lg`}></i>
        </div>
        <div className="flex-1 border-b border-gray-100 pb-2">
            <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest mb-0.5">{label}</p>
            <p className="text-gray-800 font-bold text-lg leading-tight">{value || 'N/A'}</p>
        </div>
    </div>
);

const RetailerRegistrationApp = () => {
    // File state
    const [files, setFiles] = useState({
        gstImage: null,
        aadharFrontImage: null,
        aadharBackImage: null,
        panImage: null,
        shopPhoto: null,
        visitingCard: null
    });

    // Preview URLs
    const [previews, setPreviews] = useState({
        gstImage: null,
        aadharFrontImage: null,
        aadharBackImage: null,
        panImage: null,
        shopPhoto: null,
        visitingCard: null
    });

    // UI state
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [apiError, setApiError] = useState('');
    const [isRegistered, setIsRegistered] = useState(false);
    const [registrationData, setRegistrationData] = useState(null);

    // Validation schema
    const validationSchema = Yup.object({
        // Personal Information
        firstName: Yup.string()
            .matches(/^[A-Za-z]+$/, 'Only alphabets allowed')
            .max(50, 'Max 50 characters allowed')
            .required('First name is required'),
        lastName: Yup.string()
            .matches(/^[A-Za-z]+$/, 'Only alphabets allowed')
            .max(50, 'Max 50 characters allowed')
            .required('Last name is required'),
        email: Yup.string()
            .email('Invalid email format')
            .max(100, 'Max 100 characters allowed')
            .required('Email is required'),
        phoneNumber: Yup.string()
            .matches(/^\d{10}$/, 'Phone must be exactly 10 digits')
            .required('Phone number is required'),

        // Shop Information
        shopName: Yup.string()
            .max(100, 'Max 100 characters allowed')
            .required('Shop name is required'),
        addressLine1: Yup.string()
            .max(200, 'Max 200 characters allowed')
            .required('Address is required'),
        addressLine2: Yup.string()
            .max(200, 'Max 200 characters allowed')
            .nullable(),
        landmark: Yup.string()
            .max(100, 'Max 100 characters allowed')
            .nullable(),
        area: Yup.string()
            .max(100, 'Max 100 characters allowed')
            .required('Area is required'),
        city: Yup.string()
            .matches(/^[A-Za-z\s]+$/, 'Only alphabets and spaces allowed')
            .max(50, 'Max 50 characters allowed')
            .required('City is required'),
        state: Yup.string()
            .matches(/^[A-Za-z\s]+$/, 'Only alphabets and spaces allowed')
            .max(50, 'Max 50 characters allowed')
            .required('State is required'),
        pincode: Yup.string()
            .matches(/^\d{6}$/, 'Pincode must be 6 digits')
            .required('Pincode is required'),
        remark: Yup.string()
            .max(500, 'Max 500 characters allowed'),

        // Document Type
        documentType: Yup.string().required('Please select a document type'),

        // GST fields (conditional)
        gstNumber: Yup.string().when('documentType', {
            is: 'GST',
            then: (schema) => schema
                .matches(/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/, 'Invalid GST format')
                .max(15, 'GST number cannot exceed 15 characters')
                .required('GST Number is required'),
            otherwise: (schema) => schema.notRequired()
        }),

        // Aadhar+PAN fields (conditional)
        aadharNumber: Yup.string().when('documentType', {
            is: 'AadharPan',
            then: (schema) => schema
                .matches(/^\d{12}$/, 'Aadhar Number must be exactly 12 digits')
                .required('Aadhar Number is required'),
            otherwise: (schema) => schema.notRequired()
        }),
        panNumber: Yup.string().when('documentType', {
            is: 'AadharPan',
            then: (schema) => schema
                .matches(/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/, 'PAN Number format is invalid (e.g., ABCDE1234F)')
                .max(10, 'PAN number must be 10 characters')
                .required('PAN Number is required'),
            otherwise: (schema) => schema.notRequired()
        }),

        // Passwords
        password: Yup.string()
            .min(6, 'Password must be at least 6 characters')
            .max(50, 'Password is too long')
            .required('Password is required'),
        confirmPassword: Yup.string()
            .oneOf([Yup.ref('password')], 'Password and Confirm Password do not match')
            .required('Confirm Password is required'),

        // Employee ID
        referralCode: Yup.string().max(20, 'Employee ID too long'),

        // Employee ID
        employeeId: Yup.string().max(50, 'Employee ID too long'),

        // Terms
        agreeToTerms: Yup.boolean()
            .oneOf([true], 'You must agree to the Terms and Conditions')
            .required('You must agree to the Terms and Conditions')
    });

    // Formik setup
    const formik = useFormik({
        initialValues: {
            firstName: '',
            lastName: '',
            email: '',
            phoneNumber: '',
            shopName: '',
            addressLine1: '',
            addressLine2: '',
            landmark: '',
            area: '',
            city: '',
            state: '',
            pincode: '',
            remark: '',
            documentType: 'GST',
            gstNumber: '',
            aadharNumber: '',
            panNumber: '',
            password: '',
            confirmPassword: '',
            referralCode: '',
            employeeId: '',
            agreeToTerms: false,
            subscribeToNewsletter: false
        },
        validationSchema,
        validateOnChange: true,
        validateOnBlur: true,
        onSubmit: handleFormSubmit
    });

    // Custom file validation
    const validateFiles = () => {
        const errors = {};

        // GST document validation
        if (formik.values.documentType === 'GST') {
            if (!files.gstImage) {
                errors.gstImage = 'GST Certificate image is required';
            }
        }

        // Aadhar+PAN document validation
        if (formik.values.documentType === 'AadharPan') {
            if (!files.aadharFrontImage) {
                errors.aadharFrontImage = 'Aadhar Front image is required';
            }
            if (!files.aadharBackImage) {
                errors.aadharBackImage = 'Aadhar Back image is required';
            }
            if (!files.panImage) {
                errors.panImage = 'PAN Card image is required';
            }
        }

        // Shop images validation (at least one required)
        if (!files.shopPhoto && !files.visitingCard) {
            errors.shopImages = 'At least one shop image is required (Shop Photo or Visiting Card)';
        }

        return errors;
    };

    // Handle file upload
    const handleFileChange = (field, file) => {
        if (file) {
            setFiles(prev => ({ ...prev, [field]: file }));

            // Create preview URL
            const reader = new FileReader();
            reader.onload = (e) => {
                setPreviews(prev => ({ ...prev, [field]: e.target.result }));
            };
            reader.readAsDataURL(file);
        }
    };

    // Remove file
    const removeFile = (field) => {
        setFiles(prev => ({ ...prev, [field]: null }));
        setPreviews(prev => ({ ...prev, [field]: null }));
    };

    // Parse backend error message and map to field
    const parseBackendError = (errorMessage) => {
        const message = errorMessage.toLowerCase();
        const fieldErrors = {};

        // Email errors
        if (message.includes('email') && (message.includes('already') || message.includes('allready') || message.includes('registered'))) {
            fieldErrors.email = 'Email Id allready registered';
        } else if (message.includes('email') && message.includes('invalid')) {
            fieldErrors.email = errorMessage;
        }

        // Phone errors
        if (message.includes('phone') && (message.includes('already') || message.includes('allready') || message.includes('registered'))) {
            fieldErrors.phoneNumber = 'Phone number allready registered';
        } else if (message.includes('phone') && message.includes('invalid')) {
            fieldErrors.phoneNumber = errorMessage;
        }

        // Password errors
        if (message.includes('password')) {
            fieldErrors.password = errorMessage;
        }

        // Employee ID errors
        if (message.includes('employee') && message.includes('invalid')) {
            fieldErrors.referralCode = 'Invalid Employee ID';
        }

        // GST errors
        if (message.includes('gst')) {
            fieldErrors.gstNumber = errorMessage;
        }

        // Aadhar errors
        if (message.includes('aadhar') || message.includes('aadhaar')) {
            fieldErrors.aadharNumber = errorMessage;
        }

        // PAN errors
        if (message.includes('pan')) {
            fieldErrors.panNumber = errorMessage;
        }

        return fieldErrors;
    };

    // Handle form submission
    async function handleFormSubmit(values) {
        // Validate files
        const fileErrors = validateFiles();
        if (Object.keys(fileErrors).length > 0) {
            // Set file errors manually since Formik doesn't handle files
            formik.setErrors({ ...formik.errors, ...fileErrors });
            window.scrollTo({ top: 0, behavior: 'smooth' });
            return;
        }

        setLoading(true);
        setApiError('');

        try {
            // Create FormData for file upload
            const formDataToSend = new FormData();

            // Add all text fields with proper casing for C# model binding
            formDataToSend.append('FirstName', values.firstName);
            formDataToSend.append('LastName', values.lastName);
            formDataToSend.append('Email', values.email);
            formDataToSend.append('PhoneNumber', values.phoneNumber);
            formDataToSend.append('ShopName', values.shopName);
            formDataToSend.append('AddressLine1', values.addressLine1);
            formDataToSend.append('AddressLine2', values.addressLine2);
            formDataToSend.append('Landmark', values.landmark);
            formDataToSend.append('Area', values.area);
            formDataToSend.append('City', values.city);
            formDataToSend.append('State', values.state);
            formDataToSend.append('Pincode', values.pincode);
            formDataToSend.append('Remark', values.remark);
            formDataToSend.append('DocumentType', values.documentType);
            formDataToSend.append('GstNumber', values.gstNumber);
            formDataToSend.append('AadharNumber', values.aadharNumber);
            formDataToSend.append('PanNumber', values.panNumber);
            formDataToSend.append('Password', values.password);
            formDataToSend.append('ConfirmPassword', values.confirmPassword);
            formDataToSend.append('ReferralCode', values.referralCode);
            formDataToSend.append('EmployeeId', values.employeeId);
            formDataToSend.append('AgreeToTerms', values.agreeToTerms);
            formDataToSend.append('SubscribeToNewsletter', values.subscribeToNewsletter);

            // Add files with proper casing
            if (files.gstImage) formDataToSend.append('GstImage', files.gstImage);
            if (files.aadharFrontImage) formDataToSend.append('AadharFrontImage', files.aadharFrontImage);
            if (files.aadharBackImage) formDataToSend.append('AadharBackImage', files.aadharBackImage);
            if (files.panImage) formDataToSend.append('PanImage', files.panImage);
            if (files.shopPhoto) formDataToSend.append('ShopPhoto', files.shopPhoto);
            if (files.visitingCard) formDataToSend.append('VisitingCard', files.visitingCard);

            // Submit to API endpoint
            const response = await fetch('/Account/RegisterRetailerApi', {
                method: 'POST',
                body: formDataToSend
            });

            const result = await response.json();
            console.log('Registration Response:', result);

            if (result.success) {
                console.log('Success - showing success page with data:', result.data || result.Data);
                // Success - instead of redirecting immediately, show success screen
                setRegistrationData(result.data || result.Data);
                setIsRegistered(true);
                window.scrollTo({ top: 0, behavior: 'smooth' });
            } else {
                // Parse backend error and set field-specific errors
                const fieldErrors = parseBackendError(result.message || '');

                console.log('Backend error message:', result.message);
                console.log('Parsed field errors:', fieldErrors);

                // Set field-specific errors using setFieldError for each field
                if (Object.keys(fieldErrors).length > 0) {
                    Object.keys(fieldErrors).forEach(field => {
                        formik.setFieldError(field, fieldErrors[field]);
                        formik.setFieldTouched(field, true, false);
                    });
                }

                // Always show the error message at the top as well
                setApiError(result.message || 'Registration failed. Please try again.');
                window.scrollTo({ top: 0, behavior: 'smooth' });
            }
        } catch (error) {
            console.error('Registration error:', error);
            setApiError('An error occurred during registration. Please try again.');
            window.scrollTo({ top: 0, behavior: 'smooth' });
        } finally {
            setLoading(false);
        }
    }

    if (isRegistered && registrationData) {
        return <SuccessPage data={registrationData} />;
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-pink-50 py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto">
                {/* Header */}
                <div className="text-center mb-8">
                    <div className="mb-6">
                        <i className="fas fa-store text-primary-500 text-6xl"></i>
                    </div>
                    <h2 className="text-4xl font-bold text-gray-900 mb-3">
                        Registration
                    </h2>
                </div>

                {/* Registration Form */}
                <div className="bg-white rounded-2xl shadow-xl p-8">
                    <form onSubmit={formik.handleSubmit} noValidate className="space-y-8">
                        {/* API Error Alert */}
                        {apiError && (
                            <div className="bg-red-50 border-l-4 border-red-500 text-red-700 p-4 rounded-lg">
                                <div className="flex items-start">
                                    <i className="fas fa-exclamation-circle text-red-500 mt-1 mr-3"></i>
                                    <p>{apiError}</p>
                                </div>
                            </div>
                        )}

                        {/* Personal Information */}
                        <div className="border-b border-gray-200 pb-6">
                            <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                                <i className="fas fa-user-circle text-primary-500 mr-3"></i>
                                Personal Information
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">First Name <span className="text-red-600" style={{ color: 'red' }}>*</span></label>
                                    <input
                                        type="text"
                                        name="firstName"
                                        value={formik.values.firstName}
                                        onChange={(e) => {
                                            const val = e.target.value.replace(/[^A-Za-z\s]/g, '');
                                            formik.setFieldValue('firstName', val);
                                        }}
                                        onKeyDown={(e) => {
                                            if (e.key >= '0' && e.key <= '9') {
                                                e.preventDefault();
                                            }
                                        }}
                                        onBlur={formik.handleBlur}
                                        maxLength="50"
                                        className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${formik.touched.firstName && formik.errors.firstName ? 'border-red-500' : 'border-gray-300'}`}
                                        placeholder="Enter first name"
                                    />
                                    {formik.touched.firstName && formik.errors.firstName && (
                                        <p className="text-red-500 text-sm mt-1">{formik.errors.firstName}</p>
                                    )}
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Last Name <span className="text-red-600" style={{ color: 'red' }}>*</span></label>
                                    <input
                                        type="text"
                                        name="lastName"
                                        value={formik.values.lastName}
                                        onChange={(e) => {
                                            const val = e.target.value.replace(/[^A-Za-z\s]/g, '');
                                            formik.setFieldValue('lastName', val);
                                        }}
                                        onKeyDown={(e) => {
                                            if (e.key >= '0' && e.key <= '9') {
                                                e.preventDefault();
                                            }
                                        }}
                                        onBlur={formik.handleBlur}
                                        maxLength="50"
                                        className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${formik.touched.lastName && formik.errors.lastName ? 'border-red-500' : 'border-gray-300'}`}
                                        placeholder="Enter last name"
                                    />
                                    {formik.touched.lastName && formik.errors.lastName && (
                                        <p className="text-red-500 text-sm mt-1">{formik.errors.lastName}</p>
                                    )}
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Email Address <span className="text-red-600" style={{ color: 'red' }}>*</span></label>
                                    <input
                                        type="email"
                                        name="email"
                                        value={formik.values.email}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        maxLength="100"
                                        className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${formik.touched.email && formik.errors.email ? 'border-red-500' : 'border-gray-300'}`}
                                        placeholder="your@email.com"
                                    />
                                    {formik.touched.email && formik.errors.email && (
                                        <p className="text-red-500 text-sm mt-1">{formik.errors.email}</p>
                                    )}
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number <span className="text-red-600" style={{ color: 'red' }}>*</span></label>
                                    <input
                                        type="tel"
                                        name="phoneNumber"
                                        value={formik.values.phoneNumber}
                                        onChange={(e) => {
                                            const val = e.target.value.replace(/\D/g, '').substring(0, 10);
                                            formik.setFieldValue('phoneNumber', val);
                                        }}
                                        onKeyDown={(e) => {
                                            // Allow backspace, delete, tab, escape, enter
                                            if ([46, 8, 9, 27, 13].includes(e.keyCode) ||
                                                // Allow Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
                                                (e.ctrlKey === true) ||
                                                // Allow home, end, left, right
                                                (e.keyCode >= 35 && e.keyCode <= 39)) {
                                                return;
                                            }
                                            // Block if not a number
                                            if ((e.shiftKey || (e.keyCode < 48 || e.keyCode > 57)) && (e.keyCode < 96 || e.keyCode > 105)) {
                                                e.preventDefault();
                                            }
                                        }}
                                        onBlur={formik.handleBlur}
                                        maxLength="10"
                                        className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${formik.touched.phoneNumber && formik.errors.phoneNumber ? 'border-red-500' : 'border-gray-300'}`}
                                        placeholder="10-digit mobile number"
                                    />
                                    {formik.touched.phoneNumber && formik.errors.phoneNumber && (
                                        <p className="text-red-500 text-sm mt-1">{formik.errors.phoneNumber}</p>
                                    )}
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Employee ID (Optional)</label>
                                    <input
                                        type="text"
                                        name="employeeId"
                                        value={formik.values.employeeId}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        maxLength="50"
                                        className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${formik.touched.employeeId && formik.errors.employeeId ? 'border-red-500' : 'border-gray-300'}`}
                                        placeholder="Enter employee ID if any"
                                    />
                                    {formik.touched.employeeId && formik.errors.employeeId && (
                                        <p className="text-red-500 text-sm mt-1">{formik.errors.employeeId}</p>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Shop Information */}
                        <div className="border-b border-gray-200 pb-6">
                            <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                                <i className="fas fa-store text-primary-500 mr-3"></i>
                                Shop Information
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="md:col-span-2">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Shop Name <span className="text-red-600" style={{ color: 'red' }}>*</span></label>
                                    <input
                                        type="text"
                                        name="shopName"
                                        value={formik.values.shopName}
                                        onChange={(e) => {
                                            // Shop names usually allow numbers, but if the user wants purely alphabet,
                                            // we could restrict it. However, I'll allow alphanumeric but NO special chars
                                            // or just check if they meant "Any field named Name"
                                            formik.handleChange(e);
                                        }}
                                        onBlur={formik.handleBlur}
                                        maxLength="100"
                                        className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${formik.touched.shopName && formik.errors.shopName ? 'border-red-500' : 'border-gray-300'}`}
                                        placeholder="Enter your shop name"
                                    />
                                    {formik.touched.shopName && formik.errors.shopName && (
                                        <p className="text-red-500 text-sm mt-1">{formik.errors.shopName}</p>
                                    )}
                                </div>
                                <div className="md:col-span-2">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Address Line 1 <span className="text-red-600" style={{ color: 'red' }}>*</span></label>
                                    <input
                                        type="text"
                                        name="addressLine1"
                                        value={formik.values.addressLine1}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        maxLength="200"
                                        className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${formik.touched.addressLine1 && formik.errors.addressLine1 ? 'border-red-500' : 'border-gray-300'}`}
                                        placeholder="Street address, building number"
                                    />
                                    {formik.touched.addressLine1 && formik.errors.addressLine1 && (
                                        <p className="text-red-500 text-sm mt-1">{formik.errors.addressLine1}</p>
                                    )}
                                </div>
                                <div className="md:col-span-2">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Address Line 2</label>
                                    <input
                                        type="text"
                                        name="addressLine2"
                                        value={formik.values.addressLine2}
                                        onChange={formik.handleChange}
                                        maxLength="200"
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                                        placeholder="Apartment, suite, floor (optional)"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Landmark</label>
                                    <input
                                        type="text"
                                        name="landmark"
                                        value={formik.values.landmark}
                                        onChange={formik.handleChange}
                                        maxLength="100"
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                                        placeholder="Nearby landmark"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Area <span className="text-red-600" style={{ color: 'red' }}>*</span></label>
                                    <input
                                        type="text"
                                        name="area"
                                        value={formik.values.area}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        maxLength="100"
                                        className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${formik.touched.area && formik.errors.area ? 'border-red-500' : 'border-gray-300'}`}
                                        placeholder="Area/Locality"
                                    />
                                    {formik.touched.area && formik.errors.area && (
                                        <p className="text-red-500 text-sm mt-1">{formik.errors.area}</p>
                                    )}
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">City <span className="text-red-600" style={{ color: 'red' }}>*</span></label>
                                    <input
                                        type="text"
                                        name="city"
                                        value={formik.values.city}
                                        onChange={(e) => {
                                            const val = e.target.value.replace(/[^A-Za-z\s]/g, '');
                                            formik.setFieldValue('city', val);
                                        }}
                                        onBlur={formik.handleBlur}
                                        maxLength="50"
                                        className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${formik.touched.city && formik.errors.city ? 'border-red-500' : 'border-gray-300'}`}
                                        placeholder="City"
                                    />
                                    {formik.touched.city && formik.errors.city && (
                                        <p className="text-red-500 text-sm mt-1">{formik.errors.city}</p>
                                    )}
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">State <span className="text-red-600" style={{ color: 'red' }}>*</span></label>
                                    <input
                                        type="text"
                                        name="state"
                                        value={formik.values.state}
                                        onChange={(e) => {
                                            const val = e.target.value.replace(/[^A-Za-z\s]/g, '');
                                            formik.setFieldValue('state', val);
                                        }}
                                        onBlur={formik.handleBlur}
                                        maxLength="50"
                                        className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${formik.touched.state && formik.errors.state ? 'border-red-500' : 'border-gray-300'}`}
                                        placeholder="State"
                                    />
                                    {formik.touched.state && formik.errors.state && (
                                        <p className="text-red-500 text-sm mt-1">{formik.errors.state}</p>
                                    )}
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Pincode <span className="text-red-600" style={{ color: 'red' }}>*</span></label>
                                    <input
                                        type="text"
                                        name="pincode"
                                        value={formik.values.pincode}
                                        onChange={(e) => {
                                            const val = e.target.value.replace(/\D/g, '').substring(0, 6);
                                            formik.setFieldValue('pincode', val);
                                        }}
                                        onKeyDown={(e) => {
                                            if ([46, 8, 9, 27, 13].includes(e.keyCode) || (e.ctrlKey === true) || (e.keyCode >= 35 && e.keyCode <= 39)) return;
                                            if ((e.shiftKey || (e.keyCode < 48 || e.keyCode > 57)) && (e.keyCode < 96 || e.keyCode > 105)) e.preventDefault();
                                        }}
                                        onBlur={formik.handleBlur}
                                        maxLength="6"
                                        className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${formik.touched.pincode && formik.errors.pincode ? 'border-red-500' : 'border-gray-300'}`}
                                        placeholder="6-digit pincode"
                                    />
                                    {formik.touched.pincode && formik.errors.pincode && (
                                        <p className="text-red-500 text-sm mt-1">{formik.errors.pincode}</p>
                                    )}
                                </div>
                                <div className="md:col-span-2">
                                    <div className="flex justify-between items-center mb-2">
                                        <label className="block text-sm font-medium text-gray-700">Remarks</label>
                                        <span className={`text-xs ${formik.values.remark?.length > 500 ? 'text-red-500' : 'text-gray-500'}`}>
                                            {formik.values.remark?.length || 0}/500
                                        </span>
                                    </div>
                                    <textarea
                                        name="remark"
                                        value={formik.values.remark}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        rows="3"
                                        maxLength="500"
                                        className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${formik.touched.remark && formik.errors.remark ? 'border-red-500' : 'border-gray-300'}`}
                                        placeholder="Any additional information"
                                    />
                                    {formik.touched.remark && formik.errors.remark && (
                                        <p className="text-red-500 text-sm mt-1">{formik.errors.remark}</p>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Document Verification */}
                        <div className="border-b border-gray-200 pb-6">
                            <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                                <i className="fas fa-file-alt text-primary-500 mr-3"></i>
                                Document Verification
                            </h3>

                            {/* Document Type Selection */}
                            <div className="mb-6">
                                <label className="block text-sm font-medium text-gray-700 mb-3">Document Type <span className="text-red-600" style={{ color: 'red' }}>*</span></label>
                                <div className="flex gap-6">
                                    <div className="flex items-center">
                                        <input
                                            type="radio"
                                            id="docTypeGST"
                                            name="documentType"
                                            value="GST"
                                            checked={formik.values.documentType === 'GST'}
                                            onChange={formik.handleChange}
                                            className="h-5 w-5 text-primary-500 focus:ring-primary-500 border-gray-300"
                                        />
                                        <label htmlFor="docTypeGST" className="ml-3 text-sm font-medium text-gray-700">
                                            GST Certificate
                                        </label>
                                    </div>
                                    <div className="flex items-center">
                                        <input
                                            type="radio"
                                            id="docTypeAadharPan"
                                            name="documentType"
                                            value="AadharPan"
                                            checked={formik.values.documentType === 'AadharPan'}
                                            onChange={formik.handleChange}
                                            className="h-5 w-5 text-primary-500 focus:ring-primary-500 border-gray-300"
                                        />
                                        <label htmlFor="docTypeAadharPan" className="ml-3 text-sm font-medium text-gray-700">
                                            Aadhar + PAN
                                        </label>
                                    </div>
                                </div>
                            </div>

                            {/* GST Section */}
                            {formik.values.documentType === 'GST' && (
                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">GST Number <span className="text-red-600" style={{ color: 'red' }}>*</span></label>
                                        <input
                                            type="text"
                                            name="gstNumber"
                                            value={formik.values.gstNumber}
                                            onChange={(e) => formik.setFieldValue('gstNumber', e.target.value.toUpperCase())}
                                            onBlur={formik.handleBlur}
                                            maxLength="15"
                                            className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${formik.touched.gstNumber && formik.errors.gstNumber ? 'border-red-500' : 'border-gray-300'}`}
                                            placeholder="Enter 15-digit GST Number"
                                            style={{ textTransform: 'uppercase' }}
                                        />
                                        {formik.touched.gstNumber && formik.errors.gstNumber && (
                                            <p className="text-red-500 text-sm mt-1">{formik.errors.gstNumber}</p>
                                        )}
                                    </div>
                                    <FileUploadBox
                                        label="GST Certificate Image"
                                        icon="fa-cloud-upload-alt"
                                        preview={previews.gstImage}
                                        onFileChange={(file) => handleFileChange('gstImage', file)}
                                        onRemove={() => removeFile('gstImage')}
                                        error={formik.errors.gstImage}
                                        required
                                    />
                                </div>
                            )}

                            {/* Aadhar + PAN Section */}
                            {formik.values.documentType === 'AadharPan' && (
                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Aadhar Number <span className="text-red-600" style={{ color: 'red' }}>*</span></label>
                                        <input
                                            type="text"
                                            name="aadharNumber"
                                            value={formik.values.aadharNumber}
                                            onChange={(e) => {
                                                const val = e.target.value.replace(/\D/g, '').substring(0, 12);
                                                formik.setFieldValue('aadharNumber', val);
                                            }}
                                            onBlur={formik.handleBlur}
                                            maxLength="12"
                                            className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${formik.touched.aadharNumber && formik.errors.aadharNumber ? 'border-red-500' : 'border-gray-300'}`}
                                            placeholder="12-digit Aadhar number"
                                        />
                                        {formik.touched.aadharNumber && formik.errors.aadharNumber && (
                                            <p className="text-red-500 text-sm mt-1">{formik.errors.aadharNumber}</p>
                                        )}
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <FileUploadBox
                                            label="Aadhar Front Image"
                                            icon="fa-image"
                                            preview={previews.aadharFrontImage}
                                            onFileChange={(file) => handleFileChange('aadharFrontImage', file)}
                                            onRemove={() => removeFile('aadharFrontImage')}
                                            error={formik.errors.aadharFrontImage}
                                            required
                                            compact
                                        />
                                        <FileUploadBox
                                            label="Aadhar Back Image"
                                            icon="fa-image"
                                            preview={previews.aadharBackImage}
                                            onFileChange={(file) => handleFileChange('aadharBackImage', file)}
                                            onRemove={() => removeFile('aadharBackImage')}
                                            error={formik.errors.aadharBackImage}
                                            required
                                            compact
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">PAN Number <span className="text-red-600" style={{ color: 'red' }}>*</span></label>
                                        <input
                                            type="text"
                                            name="panNumber"
                                            value={formik.values.panNumber}
                                            onChange={(e) => formik.setFieldValue('panNumber', e.target.value.toUpperCase().substring(0, 10))}
                                            onBlur={formik.handleBlur}
                                            maxLength="10"
                                            className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${formik.touched.panNumber && formik.errors.panNumber ? 'border-red-500' : 'border-gray-300'}`}
                                            placeholder="e.g., ABCDE1234F"
                                            style={{ textTransform: 'uppercase' }}
                                        />
                                        {formik.touched.panNumber && formik.errors.panNumber && (
                                            <p className="text-red-500 text-sm mt-1">{formik.errors.panNumber}</p>
                                        )}
                                    </div>
                                    <FileUploadBox
                                        label="PAN Card Image"
                                        icon="fa-cloud-upload-alt"
                                        preview={previews.panImage}
                                        onFileChange={(file) => handleFileChange('panImage', file)}
                                        onRemove={() => removeFile('panImage')}
                                        error={formik.errors.panImage}
                                        required
                                    />
                                </div>
                            )}
                        </div>

                        {/* Shop Images */}
                        <div className="border-b border-gray-200 pb-6">
                            <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                                <i className="fas fa-camera text-primary-500 mr-3"></i>
                                Shop Images
                            </h3>
                            <p className="text-sm text-gray-600 mb-4">Upload at least one: Shop Photo or Visiting Card</p>
                            {formik.errors.shopImages && (
                                <p className="text-red-500 text-sm mb-4">{formik.errors.shopImages}</p>
                            )}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <FileUploadBox
                                    label="Shop Photo"
                                    icon="fa-store"
                                    preview={previews.shopPhoto}
                                    onFileChange={(file) => handleFileChange('shopPhoto', file)}
                                    onRemove={() => removeFile('shopPhoto')}
                                    required
                                />
                                <FileUploadBox
                                    label="Visiting Card"
                                    icon="fa-id-card"
                                    preview={previews.visitingCard}
                                    onFileChange={(file) => handleFileChange('visitingCard', file)}
                                    onRemove={() => removeFile('visitingCard')}
                                    required
                                />
                            </div>
                        </div>

                        {/* Security & Referral */}
                        <div className="border-b border-gray-200 pb-6">
                            <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                                <i className="fas fa-lock text-primary-500 mr-3"></i>
                                Security & Referral
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Password <span className="text-red-600" style={{ color: 'red' }}>*</span></label>
                                    <div className="relative">
                                        <input
                                            type={showPassword ? 'text' : 'password'}
                                            name="password"
                                            value={formik.values.password}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            maxLength="50"
                                            className={`w-full px-4 py-3 pr-12 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${formik.touched.password && formik.errors.password ? 'border-red-500' : 'border-gray-300'}`}
                                            placeholder="Create password"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPassword(!showPassword)}
                                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                        >
                                            <i className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                                        </button>
                                    </div>
                                    {formik.touched.password && formik.errors.password && (
                                        <p className="text-red-500 text-sm mt-1">{formik.errors.password}</p>
                                    )}
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Confirm Password <span className="text-red-600" style={{ color: 'red' }}>*</span></label>
                                    <div className="relative">
                                        <input
                                            type={showConfirmPassword ? 'text' : 'password'}
                                            name="confirmPassword"
                                            value={formik.values.confirmPassword}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            className={`w-full px-4 py-3 pr-12 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${formik.touched.confirmPassword && formik.errors.confirmPassword ? 'border-red-500' : 'border-gray-300'}`}
                                            placeholder="Confirm password"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                        >
                                            <i className={`fas ${showConfirmPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                                        </button>
                                    </div>
                                    {formik.touched.confirmPassword && formik.errors.confirmPassword && (
                                        <p className="text-red-500 text-sm mt-1">{formik.errors.confirmPassword}</p>
                                    )}
                                </div>
                                <div className="md:col-span-2">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Employee ID (Optional)
                                        <span className="text-gray-500 text-xs ml-2">Enter employee ID if you have one</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="referralCode"
                                        value={formik.values.referralCode}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        maxLength="20"
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                                        placeholder="Enter employee ID"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Terms and Conditions */}
                        <div className="space-y-4">
                            <div>
                                <div className="flex items-start">
                                    <input
                                        type="checkbox"
                                        name="agreeToTerms"
                                        checked={formik.values.agreeToTerms}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        className={`mt-1 h-5 w-5 text-primary-500 focus:ring-primary-500 rounded ${formik.touched.agreeToTerms && formik.errors.agreeToTerms ? 'border-red-500' : 'border-gray-300'}`}
                                    />
                                    <label className="ml-3 text-sm text-gray-700">
                                        I agree to the <a href="#" className="text-primary-500 hover:text-primary-600 font-medium">Terms and Conditions</a> and <a href="#" className="text-primary-500 hover:text-primary-600 font-medium">Privacy Policy</a> <span className="text-red-600" style={{ color: 'red' }}>*</span>
                                    </label>
                                </div>
                                {formik.touched.agreeToTerms && formik.errors.agreeToTerms && (
                                    <p className="text-red-500 text-sm mt-1 ml-8">{formik.errors.agreeToTerms}</p>
                                )}
                            </div>

                            <div className="flex items-start">
                                <input
                                    type="checkbox"
                                    name="subscribeToNewsletter"
                                    checked={formik.values.subscribeToNewsletter}
                                    onChange={formik.handleChange}
                                    className="mt-1 h-5 w-5 text-primary-500 focus:ring-primary-500 border-gray-300 rounded"
                                />
                                <label className="ml-3 text-sm text-gray-700">
                                    Subscribe to our newsletter for updates and special offers
                                </label>
                            </div>
                        </div>

                        {/* Submit Button */}
                        <div className="flex flex-col sm:flex-row gap-4">
                            <button
                                type="submit"
                                disabled={loading}
                                className={`flex-1 bg-gradient-to-r from-primary-500 to-pink-500 text-white font-semibold py-4 px-6 rounded-lg hover:from-primary-600 hover:to-pink-600 transform hover:scale-105 transition-all duration-200 shadow-lg ${loading ? 'opacity-75 cursor-not-allowed' : ''}`}
                            >
                                <i className={`fas ${loading ? 'fa-spinner fa-spin' : 'fa-user-plus'} mr-2`}></i>
                                <span>{loading ? 'Creating Account...' : 'Register as Retailer'}</span>
                            </button>
                        </div>

                        {/* Login Link */}
                        <div className="text-center pt-6 border-t border-gray-200">
                            <p className="text-gray-600 mb-4">Already have an account?</p>
                            <a
                                href="/Account/Login"
                                className="inline-block bg-white border-2 border-primary-500 text-primary-500 font-medium py-3 px-8 rounded-lg hover:bg-primary-50 transition-all duration-200"
                            >
                                <i className="fas fa-sign-in-alt mr-2"></i>Sign In Instead
                            </a>
                        </div>
                    </form>
                </div>

                {/* Back to Home */}
                <div className="text-center mt-6">
                    <a href="/" className="text-gray-500 hover:text-gray-700 text-sm font-medium transition-colors duration-200">
                        <i className="fas fa-arrow-left mr-1"></i>Back to Home
                    </a>
                </div>
            </div>
        </div>
    );
};

// File Upload Box Component
const FileUploadBox = ({ label, icon, preview, onFileChange, onRemove, error, required = false, compact = false }) => {
    return (
        <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
                {label} {required && <span className="text-red-600" style={{ color: 'red' }}>*</span>}
            </label>
            {!preview ? (
                <label className={`flex items-center justify-center w-full px-4 ${compact ? 'py-6' : 'py-8'} border-2 border-dashed rounded-lg cursor-pointer hover:border-primary-500 transition-colors ${error ? 'border-red-500' : 'border-gray-300'}`}>
                    <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => onFileChange(e.target.files[0])}
                        className="hidden"
                    />
                    <div className="text-center">
                        <i className={`fas ${icon} ${compact ? 'text-2xl' : 'text-3xl'} text-gray-400 mb-${compact ? '1' : '2'}`}></i>
                        <p className={`${compact ? 'text-xs' : 'text-sm'} text-gray-600`}>
                            {compact ? label : `Upload ${label}`} {required && <span className="text-red-600" style={{ color: 'red' }}>*</span>}
                        </p>
                        {!compact && <p className="text-xs text-gray-500 mt-1">PNG, JPG up to 10MB</p>}
                    </div>
                </label>
            ) : (
                <div className="mt-3">
                    <img src={preview} alt={label} className={`${compact ? 'w-full' : 'max-w-xs'} rounded-lg border border-gray-300`} />
                    <button
                        type="button"
                        onClick={onRemove}
                        className={`mt-${compact ? '1' : '2'} text-red-500 ${compact ? 'text-xs' : 'text-sm'} hover:text-red-700`}
                    >
                        <i className="fas fa-trash mr-1"></i>Remove
                    </button>
                </div>
            )}
            {error && (
                <p className="text-red-500 text-sm mt-1">{error}</p>
            )}
        </div>
    );
};

// Export for webpack builds
export default RetailerRegistrationApp;
